import fs from 'fs';
import path from 'path';
import { Configuration } from 'webpack';
import { allowedWebpackDevServerOptions, isDevServerDisabled, log, pick } from '../../utils';
import { Overrides, WebpackBuildContext } from '../types';
import { statsConfig } from './stats-config';

type DevServerConfig = NonNullable<Configuration['devServer']>;
type Result = Pick<Configuration, 'devServer'> | undefined;

export function devServerConfig(context: WebpackBuildContext, overrides: Overrides): Result {
    if (
        context.build.isProduction ||
        context.build.emitExposedDependencies ||
        isDevServerDisabled()
    ) {
        return;
    }

    const devServer: DevServerConfig = {
        hot: false,
        port: 8080,
        historyApiFallback: true,
        devMiddleware: { writeToDisk: true, ...statsConfig(context, overrides) },
        headers: {
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept',
        },
        setupMiddlewares: (middlewares, devServer) => {
            if (devServer.app) {
                devServer.app.use((request, _, next) => {
                    log.info(`[${log.timestamp}]: ${request.method} ${request.url}`);
                    next();
                });
            } else {
                log.warning('webpack-dev-server app is not defined');
            }

            return middlewares;
        },
        ...getDevServerConfig(context),
    };

    /* istanbul ignore next: debug only */
    log.debug('dev-server-config', () => JSON.stringify({ devServer }, null, 2));
    return { devServer };
}

function getDevServerConfig(context: WebpackBuildContext) {
    const { bundlerConfig } = context;

    /* istanbul ignore next: debug only */
    log.debug('dev-server-config', () => JSON.stringify({ webpack: bundlerConfig }, null, 2));

    const result = pick(bundlerConfig, allowedWebpackDevServerOptions);
    if (typeof result.proxy === 'string') {
        if (fs.existsSync(result.proxy)) {
            result.proxy = require(path.resolve(result.proxy));
        } else {
            delete result.proxy;
        }
    }

    if (result.static === undefined) {
        const contentBase = bundlerConfig.contentBase;
        if (contentBase !== undefined) {
            log.warning(CONTENT_BASE_DEPRECATION_WARNING);
            if (typeof contentBase !== 'number') {
                result.static = contentBase;
            }
        } else {
            result.static = '.';
        }
    }

    return { ...result, ...bundlerConfig.devServer } as Partial<DevServerConfig>;
}

const CONTENT_BASE_DEPRECATION_WARNING = `
    DEPRECATION WARNING: webpack.contentBase in package.json is deprecated.
    Use webpack.static instead.
`
    .replace(/\n\s*/g, ' ')
    .trim();
